﻿import React, { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { registerSW } from 'virtual:pwa-register';
import { notifyServiceWorkerUpdate } from './providers/ServiceWorkerUpdateProvider';
import { supabase } from './providers/SupabaseProvider';
import { installAppErrorMonitoring } from './lib/appErrorMonitoring';
import { consumeHardReloadAfterServiceWorkerUpdate, performHardReload } from './utils/hardReload';
import App from './App.tsx';
import './index.css';

installAppErrorMonitoring({ appName: 'AbstiCare', supabase });

const UPDATE_CHECK_INTERVAL_MS = 60 * 1000;
const ENABLE_SERVICE_WORKER = String(import.meta.env.VITE_ENABLE_SERVICE_WORKER ?? 'false').toLowerCase() === 'true';
const SW_CLEANUP_RELOAD_MARKER = 'absticare-sw-cleanup-reloaded';
let hasAppliedServiceWorkerUpdate = false;
let shouldReloadAfterControllerChange = false;
let hasRegisteredControllerChangeListener = false;
let hasReloadedAfterControllerChange = false;

type UpdateServiceWorker = (reloadPage?: boolean) => Promise<void>;

function setupControllerChangeReload(): void {
  if (hasRegisteredControllerChangeListener || !('serviceWorker' in navigator)) {
    return;
  }

  hasRegisteredControllerChangeListener = true;

  navigator.serviceWorker.addEventListener('controllerchange', () => {
    if (!shouldReloadAfterControllerChange || hasReloadedAfterControllerChange) {
      return;
    }

    hasReloadedAfterControllerChange = true;
    if (consumeHardReloadAfterServiceWorkerUpdate()) {
      void performHardReload();
      return;
    }

    window.location.reload();
  });
}

function requestServiceWorkerUpdate(updateSW: UpdateServiceWorker): void {
  if (hasAppliedServiceWorkerUpdate) {
    return;
  }

  hasAppliedServiceWorkerUpdate = true;
  shouldReloadAfterControllerChange = true;

  console.info('Neue Service-Worker-Version gefunden. Update wird automatisch aktiviert.');
  void updateSW(false).catch((error) => {
    hasAppliedServiceWorkerUpdate = false;
    shouldReloadAfterControllerChange = false;
    console.error('Automatisches Service-Worker-Update fehlgeschlagen', error);

    // Fallback: Nutzer kann das Update weiterhin manuell aus dem Banner starten.
    notifyServiceWorkerUpdate(() => {
      requestServiceWorkerUpdate(updateSW);
    });
  });
}

function setupServiceWorkerUpdateChecks(registration: ServiceWorkerRegistration): void {
  const triggerUpdateCheck = () => {
    registration.update().catch((error) => {
      console.warn('Service-Worker-Updateprüfung fehlgeschlagen', error);
    });
  };

  // Trigger an immediate check and then keep checking while the app is open.
  triggerUpdateCheck();
  window.setInterval(triggerUpdateCheck, UPDATE_CHECK_INTERVAL_MS);

  // Re-check when the app becomes active again (mobile/tablet/desktop).
  window.addEventListener('focus', triggerUpdateCheck);
  window.addEventListener('online', triggerUpdateCheck);
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') {
      triggerUpdateCheck();
    }
  });
}

function setupServiceWorkerLifecycleHandlers(
  registration: ServiceWorkerRegistration,
  updateSW: UpdateServiceWorker,
): void {
  const applyWaitingUpdate = () => {
    if (registration.waiting && navigator.serviceWorker.controller) {
      requestServiceWorkerUpdate(updateSW);
    }
  };

  applyWaitingUpdate();

  registration.addEventListener('updatefound', () => {
    const installingWorker = registration.installing;
    if (!installingWorker) {
      return;
    }

    installingWorker.addEventListener('statechange', () => {
      if (installingWorker.state === 'installed' && navigator.serviceWorker.controller) {
        requestServiceWorkerUpdate(updateSW);
      }
    });
  });
}

setupControllerChangeReload();

if (ENABLE_SERVICE_WORKER) {
  const updateSW = registerSW({
    immediate: true,
    onNeedRefresh() {
      requestServiceWorkerUpdate(updateSW);
    },
    onOfflineReady() {
      console.log('App ready to work offline');
    },
    onRegisteredSW(_swUrl, registration) {
      if (registration) {
        setupServiceWorkerUpdateChecks(registration);
        setupServiceWorkerLifecycleHandlers(registration, updateSW);
      }
    },
  });
} else if (typeof window !== 'undefined' && 'serviceWorker' in navigator) {
  // Temporär für Rollout: alte SWs/Caches entfernen, um veraltete Assets sicher zu vermeiden.
  void (async () => {
    const registrations = await navigator.serviceWorker.getRegistrations();
    await Promise.all(registrations.map((registration) => registration.unregister()));

    const cacheNames = await caches.keys();
    await Promise.all(cacheNames.map((cacheName) => caches.delete(cacheName)));

    const hasController = Boolean(navigator.serviceWorker.controller);
    const needsCleanupReload = (registrations.length > 0 || hasController)
      && sessionStorage.getItem(SW_CLEANUP_RELOAD_MARKER) !== '1';

    // Einmaliger Reload nach SW-Cleanup, damit die Seite ohne alten Controller weiterläuft.
    if (needsCleanupReload) {
      sessionStorage.setItem(SW_CLEANUP_RELOAD_MARKER, '1');
      window.location.replace(`${window.location.pathname}${window.location.search}${window.location.hash}`);
      return;
    }

    if (!hasController) {
      sessionStorage.removeItem(SW_CLEANUP_RELOAD_MARKER);
    }
  })().catch((error) => {
    console.warn('Service-Worker-Cleanup fehlgeschlagen', error);
  });
}

const rootElement = document.getElementById('root');
if (!rootElement) throw new Error('Failed to find the root element');

createRoot(rootElement).render(
  <StrictMode>
    <App />
  </StrictMode>
);
